/**
 * Filtered Data Hook
 *
 * Provides utilities for filtering data based on URL parameters
 * and common filter patterns.
 */

import { useMemo } from 'react';
import type { FilterParams, ParsedFilters } from '../data/types';

// =============================================================================
// TYPES
// =============================================================================

export interface FilterConfig<T> {
  /** Field to use for full-text search */
  searchFields?: (keyof T)[];
  /** Field containing tags array */
  tagsField?: keyof T;
  /** Field containing project ID */
  projectField?: keyof T;
  /** Field containing customer ID */
  customerField?: keyof T;
  /** Field containing date */
  dateField?: keyof T;
  /** Field containing status */
  statusField?: keyof T;
  /** Field containing priority */
  priorityField?: keyof T;
  /** Field containing channel */
  channelField?: keyof T;
  /** Field containing category */
  categoryField?: keyof T;
  /** Field containing assignee(s) */
  assigneeField?: keyof T;
  /** Custom filter function */
  customFilter?: (item: T, filters: ParsedFilters) => boolean;
}

// Helper type to allow indexing
type Indexable = { [key: string]: unknown };

export interface UseFilteredDataResult<T> {
  /** Filtered data */
  data: T[];
  /** Total count before filtering */
  totalCount: number;
  /** Filtered count */
  filteredCount: number;
  /** Whether any filters are active */
  isFiltered: boolean;
}

// =============================================================================
// IMPLEMENTATION
// =============================================================================

/**
 * Apply filters to an array of data
 */
export function applyFilters<T>(
  data: T[],
  filters: ParsedFilters,
  config: FilterConfig<T>
): T[] {
  return data.filter((item) => {
    const indexableItem = item as unknown as Indexable;

    // Full-text search
    if (filters.search && config.searchFields) {
      const searchLower = filters.search.toLowerCase();
      const matchesSearch = config.searchFields.some((field) => {
        const value = indexableItem[field as string];
        if (typeof value === 'string') {
          return value.toLowerCase().includes(searchLower);
        }
        return false;
      });
      if (!matchesSearch) return false;
    }

    // Tag filter
    if (filters.tagId && config.tagsField) {
      const tags = indexableItem[config.tagsField as string];
      if (Array.isArray(tags)) {
        if (!tags.includes(filters.tagId)) return false;
      } else {
        return false;
      }
    }

    // Project filter
    if (filters.projectId && config.projectField) {
      if (indexableItem[config.projectField as string] !== filters.projectId) return false;
    }

    // Customer filter
    if (filters.customerId && config.customerField) {
      if (indexableItem[config.customerField as string] !== filters.customerId) return false;
    }

    // Date filter
    if (filters.date && config.dateField) {
      const itemDate = indexableItem[config.dateField as string];
      if (typeof itemDate === 'string') {
        const itemDateStr = itemDate.split('T')[0];
        const filterDateStr = filters.date.toISOString().split('T')[0];
        if (itemDateStr !== filterDateStr) return false;
      }
    }

    // Date range filter
    if ((filters.dateRange.from || filters.dateRange.to) && config.dateField) {
      const itemDate = indexableItem[config.dateField as string];
      if (typeof itemDate === 'string') {
        const date = new Date(itemDate);
        if (filters.dateRange.from && date < filters.dateRange.from) return false;
        if (filters.dateRange.to && date > filters.dateRange.to) return false;
      }
    }

    // Status filter
    if (filters.status && config.statusField) {
      if (indexableItem[config.statusField as string] !== filters.status) return false;
    }

    // Priority filter
    if (filters.priority && config.priorityField) {
      if (indexableItem[config.priorityField as string] !== filters.priority) return false;
    }

    // Channel filter
    if (filters.channel && config.channelField) {
      if (indexableItem[config.channelField as string] !== filters.channel) return false;
    }

    // Category filter
    if (filters.category && config.categoryField) {
      if (indexableItem[config.categoryField as string] !== filters.category) return false;
    }

    // Assignee filter
    if (filters.assigneeId && config.assigneeField) {
      const assignees = indexableItem[config.assigneeField as string];
      if (Array.isArray(assignees)) {
        if (!assignees.includes(filters.assigneeId)) return false;
      } else if (assignees !== filters.assigneeId) {
        return false;
      }
    }

    // Custom filter
    if (config.customFilter) {
      if (!config.customFilter(item, filters)) return false;
    }

    return true;
  });
}

/**
 * Hook for filtering data based on URL parameters
 */
export function useFilteredData<T>(
  data: T[],
  filters: ParsedFilters,
  config: FilterConfig<T>
): UseFilteredDataResult<T> {
  const filteredData = useMemo(
    () => applyFilters(data, filters, config),
    [data, filters, config]
  );

  const isFiltered = useMemo(() => {
    return (
      !!filters.search ||
      !!filters.tagId ||
      !!filters.projectId ||
      !!filters.customerId ||
      !!filters.date ||
      !!filters.dateRange.from ||
      !!filters.dateRange.to ||
      !!filters.status ||
      !!filters.priority ||
      !!filters.channel ||
      !!filters.category ||
      !!filters.assigneeId
    );
  }, [filters]);

  return {
    data: filteredData,
    totalCount: data.length,
    filteredCount: filteredData.length,
    isFiltered,
  };
}

// =============================================================================
// PRE-CONFIGURED FILTER HOOKS
// =============================================================================

import type {
  Project,
  Task,
  Contact,
  Deal,
  Customer,
  Invoice,
  CalendarEvent,
  Activity,
} from '../data/types';

/**
 * Filter projects with common configuration
 */
export function useFilteredProjects(
  projects: Project[],
  filters: ParsedFilters
): UseFilteredDataResult<Project> {
  return useFilteredData(projects, filters, {
    searchFields: ['title', 'description', 'clientName'],
    tagsField: 'tags',
    customerField: 'clientId',
    statusField: 'status',
    priorityField: 'priority',
  });
}

/**
 * Filter tasks with common configuration
 */
export function useFilteredTasks(
  tasks: Task[],
  filters: ParsedFilters
): UseFilteredDataResult<Task> {
  return useFilteredData(tasks, filters, {
    searchFields: ['title', 'description'],
    tagsField: 'tags',
    projectField: 'projectId',
    dateField: 'dueAt',
    statusField: 'status',
    priorityField: 'priority',
    assigneeField: 'assignees',
  });
}

/**
 * Filter contacts with common configuration
 */
export function useFilteredContacts(
  contacts: Contact[],
  filters: ParsedFilters
): UseFilteredDataResult<Contact> {
  return useFilteredData(contacts, filters, {
    searchFields: ['displayName', 'company', 'role'],
    tagsField: 'tags',
    customFilter: (contact: Contact, f) => {
      // Also search in handles
      if (f.search) {
        const searchLower = f.search.toLowerCase();
        if (contact.handles.email?.toLowerCase().includes(searchLower)) return true;
        if (contact.handles.phone?.toLowerCase().includes(searchLower)) return true;
      }
      return true;
    },
  });
}

/**
 * Filter deals with common configuration
 */
export function useFilteredDeals(
  deals: Deal[],
  filters: ParsedFilters
): UseFilteredDataResult<Deal> {
  return useFilteredData(deals, filters, {
    searchFields: ['title', 'description', 'contactName'],
    tagsField: 'tags',
    projectField: 'projectId',
    statusField: 'stage',
    priorityField: 'priority',
    assigneeField: 'assigneeId',
  });
}

/**
 * Filter customers with common configuration
 */
export function useFilteredCustomers(
  customers: Customer[],
  filters: ParsedFilters
): UseFilteredDataResult<Customer> {
  return useFilteredData(customers, filters, {
    searchFields: ['companyName', 'firstName', 'lastName', 'email'],
    tagsField: 'tags',
    customFilter: (customer: Customer, f) => {
      // Also search in VAT number and fiscal code
      if (f.search) {
        const searchLower = f.search.toLowerCase();
        if (customer.vatNumber?.toLowerCase().includes(searchLower)) return true;
        if (customer.fiscalCode?.toLowerCase().includes(searchLower)) return true;
      }
      return true;
    },
  });
}

/**
 * Filter invoices with common configuration
 */
export function useFilteredInvoices(
  invoices: Invoice[],
  filters: ParsedFilters
): UseFilteredDataResult<Invoice> {
  return useFilteredData(invoices, filters, {
    searchFields: ['number', 'customerName', 'projectName'],
    customerField: 'customerId',
    projectField: 'projectId',
    dateField: 'date',
    statusField: 'status',
  });
}

/**
 * Filter calendar events with common configuration
 */
export function useFilteredEvents(
  events: CalendarEvent[],
  filters: ParsedFilters
): UseFilteredDataResult<CalendarEvent> {
  return useFilteredData(events, filters, {
    searchFields: ['title', 'description', 'location'],
    projectField: 'projectId',
    dateField: 'startDate',
    categoryField: 'category',
  });
}

/**
 * Filter activities with common configuration
 */
export function useFilteredActivities(
  activities: Activity[],
  filters: ParsedFilters
): UseFilteredDataResult<Activity> {
  return useFilteredData(activities, filters, {
    searchFields: ['title', 'description'],
    projectField: 'projectId',
    dateField: 'scheduledAt',
    categoryField: 'activityType',
    assigneeField: 'assigneeId',
  });
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Create ParsedFilters from FilterParams
 */
export function createParsedFilters(params: FilterParams): ParsedFilters {
  return {
    search: params.s || '',
    tagId: params.tag || null,
    projectId: params.project || null,
    customerId: params.customer || null,
    dateRange: {
      from: params.from ? new Date(params.from) : null,
      to: params.to ? new Date(params.to) : null,
    },
    date: params.date ? new Date(params.date) : null,
    status: params.status || null,
    channel: params.channel || null,
    category: params.category || null,
    priority: params.priority || null,
    assigneeId: params.assignee || null,
  };
}

/**
 * Check if filters are empty
 */
export function isEmptyFilters(filters: ParsedFilters): boolean {
  return (
    !filters.search &&
    !filters.tagId &&
    !filters.projectId &&
    !filters.customerId &&
    !filters.date &&
    !filters.dateRange.from &&
    !filters.dateRange.to &&
    !filters.status &&
    !filters.channel &&
    !filters.category &&
    !filters.priority &&
    !filters.assigneeId
  );
}

/**
 * Get active filter count
 */
export function getActiveFilterCount(filters: ParsedFilters): number {
  let count = 0;
  if (filters.search) count++;
  if (filters.tagId) count++;
  if (filters.projectId) count++;
  if (filters.customerId) count++;
  if (filters.date) count++;
  if (filters.dateRange.from || filters.dateRange.to) count++;
  if (filters.status) count++;
  if (filters.channel) count++;
  if (filters.category) count++;
  if (filters.priority) count++;
  if (filters.assigneeId) count++;
  return count;
}
